/* Absturz Supply — Announcement Slider */

class AbsSlider {
  constructor(el) {
    this.el       = el;
    this.track    = el.querySelector('.abs-bar__track');
    this.wrap     = el.querySelector('.abs-bar__track-wrap');
    this.slides   = Array.from(el.querySelectorAll('.abs-bar__slide'));
    this.prevBtn  = el.querySelector('.abs-bar__arrow--prev');
    this.nextBtn  = el.querySelector('.abs-bar__arrow--next');

    this.count       = this.slides.length;
    this.current     = 0;
    this.busy        = false;
    this.timer       = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ds        = el.dataset;
    this.type       = ds.transition || 'fade';
    this.autoplayOn = ds.autoplay === 'true';
    this.interval   = parseInt(ds.interval, 10) || 4000;
    this.pauseHover = ds.pauseOnHover === 'true';
    this.duration   = parseInt(
      getComputedStyle(el).getPropertyValue('--abs-transition-duration'), 10
    ) || 350;

    if (this.count < 2) {
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
    }

    this._bind();
    if (this.type === 'slide') this._initSlide();
    if (this.autoplayOn && this.count > 1 && !this.reduceMotion) this._play();
  }

  /* ── Slide init / resize ── */
  _initSlide() {
    this._resizeSlide();
    this._resizeHandler = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._resizeSlide(), 80);
    };
    window.addEventListener('resize', this._resizeHandler, { passive: true });
  }

  _resizeSlide() {
    const w = this.wrap.offsetWidth;
    this.track.style.transition = 'none';
    this.track.style.width      = (w * this.count) + 'px';
    this.slides.forEach(s => { s.style.width = w + 'px'; });
    this.track.style.transform  = `translateX(${-this.current * w}px)`;
  }

  /* ── Navigation ── */
  prev() { this._go(this.current - 1); this._restart(); }
  next() { this._go(this.current + 1); this._restart(); }

  _go(to) {
    if (this.busy || this.count < 2) return;
    const prev   = this.current;
    this.current = ((to % this.count) + this.count) % this.count;
    if (this.current === prev) return;
    this.busy = true;
    this.type === 'slide' ? this._doSlide() : this._doFade(prev);
    setTimeout(() => { this.busy = false; }, this.duration + 20);
  }

  _doFade(prev) {
    const dur = this.reduceMotion ? 0 : this.duration;
    const from = this.slides[prev];
    const to   = this.slides[this.current];

    from.style.transition = `opacity ${dur}ms ease`;
    from.style.opacity    = '0';

    setTimeout(() => {
      from.classList.remove('abs-bar__slide--active');
      from.setAttribute('aria-hidden', 'true');
      from.style.opacity    = '';
      from.style.transition = '';

      to.classList.add('abs-bar__slide--active');
      to.setAttribute('aria-hidden', 'false');
    }, dur);
  }

  _doSlide() {
    const dur = this.reduceMotion ? 0 : this.duration;
    const w   = this.wrap.offsetWidth;
    this.track.style.transition = `transform ${dur}ms ease`;
    this.track.style.transform  = `translateX(${-this.current * w}px)`;
    this.slides.forEach((s, i) => {
      s.classList.toggle('abs-bar__slide--active', i === this.current);
      s.setAttribute('aria-hidden', i === this.current ? 'false' : 'true');
    });
  }

  /* ── Autoplay ── */
  _play()    { this._stop(); this.timer = setInterval(() => this._go(this.current + 1), this.interval); }
  _stop()    { clearInterval(this.timer); this.timer = null; }
  _restart() { if (this.autoplayOn && !this.reduceMotion) this._play(); }

  /* ── Theme Editor ── */
  jumpTo(blockId) {
    const idx = this.slides.findIndex(s => s.dataset.blockId === blockId);
    if (idx !== -1 && idx !== this.current) {
      this._stop();
      const prev   = this.current;
      this.current = idx;
      this.busy    = false;
      this.type === 'slide' ? this._doSlide() : this._doFade(prev);
      setTimeout(() => { this.busy = false; }, this.duration + 20);
    }
  }

  /* ── Event binding ── */
  _bind() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    if (this.pauseHover) {
      this.el.addEventListener('mouseenter', () => this._stop());
      this.el.addEventListener('mouseleave', () => this._restart());
      this.el.addEventListener('focusin',    () => this._stop());
      this.el.addEventListener('focusout',   e => {
        if (!this.el.contains(e.relatedTarget)) this._restart();
      });
    }
  }

  destroy() {
    this._stop();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  }
}

/* ── Registry ── */
const ABS_MAP = new Map();

function absInit(root) {
  const bars = root.id?.startsWith('abs-bar-')
    ? [root]
    : Array.from(root.querySelectorAll('[id^="abs-bar-"]'));

  bars.forEach(bar => {
    const existing = ABS_MAP.get(bar.id);
    if (existing) { existing.destroy(); ABS_MAP.delete(bar.id); }
    ABS_MAP.set(bar.id, new AbsSlider(bar));
  });
}

function absDestroy(root) {
  (root.id?.startsWith('abs-bar-') ? [root] : Array.from(root.querySelectorAll('[id^="abs-bar-"]')))
    .forEach(bar => {
      ABS_MAP.get(bar.id)?.destroy();
      ABS_MAP.delete(bar.id);
    });
}

/* Initial load */
document.querySelectorAll('[id^="abs-bar-"]').forEach(bar => {
  ABS_MAP.set(bar.id, new AbsSlider(bar));
});

/* Theme Editor */
document.addEventListener('shopify:section:load',   e => absInit(e.target));
document.addEventListener('shopify:section:unload', e => absDestroy(e.target));

document.addEventListener('shopify:block:select', e => {
  const blockId = e.detail?.blockId;
  const bar     = e.target.querySelector('[id^="abs-bar-"]');
  if (!bar || !blockId) return;
  ABS_MAP.get(bar.id)?.jumpTo(blockId);
});

document.addEventListener('shopify:block:deselect', e => {
  const bar = e.target.querySelector('[id^="abs-bar-"]');
  if (!bar) return;
  const inst = ABS_MAP.get(bar.id);
  if (inst?.autoplayOn) inst._play();
});
